
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { familyInviteService } from '@/services/family';
import { toast } from 'sonner';
import { FAMILY_INVITE_CODE, getEntityKey } from '@/constants/queryKeys';

/**
 * Hook to fetch and manage user invite code
 */
export const useFamilyInviteCode = (userId: string | null | undefined) => {
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  
  const {
    data: inviteCode,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: getEntityKey(FAMILY_INVITE_CODE, userId),
    queryFn: async () => {
      if (!userId) return null;
      return familyInviteService.getFamilyInviteCode(userId);
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const generateNewInviteCode = async () => {
    if (!userId) return null;
    
    setIsGeneratingCode(true);
    try {
      const newCode = await familyInviteService.generateNewInviteCode(userId);
      if (newCode) {
        toast.success('New invite code generated');
        refetch();
        return newCode;
      } else {
        toast.error('Failed to generate new invite code');
        return null;
      }
    } catch (error) {
      console.error('Error generating invite code:', error);
      toast.error('Failed to generate new invite code');
      return null;
    } finally {
      setIsGeneratingCode(false);
    }
  };

  return {
    inviteCode,
    isGeneratingCode,
    generateNewInviteCode,
    isLoading,
    error: error ? String(error) : null
  };
};
